import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { API_BASE_URL } from './api.config';
import { AuthService } from '@/features/auth';

// Global type declaration for notification function
declare global {
  interface Window {
    showSessionConflictNotification?: (message: string) => void;
  }
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor to add token
    this.api.interceptors.request.use(
      (config) => {
        const token = Cookies.get('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle password change requirement
        if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
          // Dispatch custom event to trigger password change modal
          window.dispatchEvent(new CustomEvent('apiError', {
            detail: { code: 'PASSWORD_CHANGE_REQUIRED' }
          }));
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          const errorMessage = error.response?.data?.error || error.response?.data?.message;

          // Check if it's a session conflict (user logged in from another device)
          if (errorMessage && errorMessage.includes('logged in from another device')) {
            // Don't handle session conflicts here - let the AuthContext polling handle it
            // This prevents duplicate notifications
            return Promise.reject(error);
          } else {
            // Regular token expiration
            await AuthService.logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }
}

export const apiService = new ApiService();
